/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'

export const cardStyles = (theme: Theme, isPinned: boolean) => css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 12px 10px;
  border-radius: 10px;
  border: 1px solid
    ${isPinned ? alpha(theme.palette.primary.main, 0.32) : alpha(theme.palette.text.primary, 0.18)};
  background-color: ${isPinned
    ? alpha(theme.palette.primary.main, 0.08)
    : theme.palette.background.paper};
  box-shadow: none;
  transition:
    border-color 160ms ease,
    background-color 160ms ease;

  &:hover {
    border-color: ${isPinned
      ? alpha(theme.palette.primary.main, 0.45)
      : alpha(theme.palette.text.primary, 0.28)};
  }
`

export const headerStyles = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

export const titleStyles = (theme: Theme) => css`
  flex: 1;
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
  color: ${theme.palette.text.primary};
`

export const typeChipStyles = (theme: Theme) => css`
  flex-shrink: 0;
  height: 22px;
  border-radius: 999px;
  background: ${alpha(theme.palette.text.primary, 0.08)};
  color: ${theme.palette.text.secondary};

  .MuiChip-label {
    padding-inline: 10px;
    font-size: 0.72rem;
    line-height: 1;
  }
`

export const descriptionStyles = (theme: Theme) => css`
  font-size: 0.84rem;
  color: ${theme.palette.text.secondary};
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(1.35em * 2);

  p {
    margin: 0;
  }
`

export const badgesWrapStyles = css`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`

const sharedBadgeLabelStyles = css`
  .MuiChip-label {
    padding-inline: 8px;
    font-size: 0.74rem;
    line-height: 1;
  }
`

export const neutralBadgeStyles = (theme: Theme) => css`
  ${sharedBadgeLabelStyles};
  height: 22px;
  border-radius: 6px;
  border: none;
  background: ${alpha(theme.palette.success.main, 0.12)};
  color: ${theme.palette.success.dark};
`

export const warningBadgeStyles = (theme: Theme) => css`
  ${sharedBadgeLabelStyles};
  height: 22px;
  border-radius: 6px;
  border: none;
  background: ${alpha(theme.palette.warning.main, 0.18)};
  color: ${theme.palette.warning.dark};
`

export const buttonStyles = (theme: Theme, isPinned: boolean) => css`
  align-self: flex-start;
  min-width: auto;
  margin-top: 2px;
  padding: 2px 8px;
  height: 26px;
  border-radius: 7px;
  text-transform: none;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1;
  background: ${isPinned ? theme.palette.primary.main : alpha(theme.palette.background.paper, 0.9)};
  color: ${isPinned ? theme.palette.primary.contrastText : theme.palette.text.primary};
  border: 1px solid
    ${isPinned ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.2)};
  box-shadow: none;

  &:hover {
    background: ${isPinned ? theme.palette.primary.dark : alpha(theme.palette.text.primary, 0.03)};
    box-shadow: none;
  }

  &:disabled {
    color: ${theme.palette.text.disabled};
    border-color: ${alpha(theme.palette.text.primary, 0.12)};
  }
`
